/**
 * For now, chat is the only AI feature that is surfaced through the sidepanel, so this file lives in
 * the llm/chat-library folder.
 */
import {Switch, Popover, Text} from '@mantine/core';
import classNames from 'classnames';
import React, {useState} from 'react';

import {
  DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED_PREFERENCE_KEY,
  DEFAULT_DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED,
} from 'llm/deep-research/DeepResearchConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import usePreference from 'ui-library/hooks/usePreference';

export function AISettingsContent(): JSX.Element {
  const [emailNotificationEnabledPref, setEmailNotificationEnabled] = usePreference<boolean | undefined>(
    DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED_PREFERENCE_KEY
  );
  const emailNotificationEnabled =
    emailNotificationEnabledPref ?? DEFAULT_DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED;
  return (
    <Switch
      checked={emailNotificationEnabled}
      onChange={(event) => {
        setEmailNotificationEnabled(event.currentTarget.checked);
      }}
      labelPosition="left"
      label={<Text fw="normal">Notify me by email when Deep Research reports complete</Text>}
    />
  );
}

export function AISettingsToggleButton({size}: {size: ButtonSize}): JSX.Element {
  const [isOpen, setOpen] = useState(false);

  return (
    <Popover
      position="bottom-end"
      shadow="md"
      opened={isOpen}
      onChange={setOpen}
      withinPortal={true}
      portalProps={{target: 'body'}}
      offset={{
        // Default offset lines up perfectly with the header, which makes it harder to visually interpret
        // the component as a popover.
        mainAxis: 4,
      }}
    >
      <Popover.Target>
        {/* Popover.Target must accept a ref, but Button doesn't, so wrap in a span. */}
        <span>
          <Button
            aria-label="Settings"
            buttonStyle={ButtonStyle.TERTIARY_WHITE}
            className={classNames({'is-active': isOpen})}
            icon="gear"
            onClick={() => {
              setOpen(!isOpen);
            }}
            size={size}
          />
        </span>
      </Popover.Target>
      <Popover.Dropdown>
        <AISettingsContent />
      </Popover.Dropdown>
    </Popover>
  );
}
